/**
 * Withings API Types
 * Type definitions for Withings API communication
 */

export interface WithingsMeasurementsResponse {
  /** Body containing measurements */
  body: {
    /** Measurement groups */
    measuregrps: WithingsMeasurementGroup[];
    /** Update time */
    updatetime: number;
  };
  /** Status */
  status: number;
}

// NOTE: WithingsMeasurementGroup is only used internally in WithingsMeasurementsResponse
// Reason: Not exported as it's an internal implementation detail
interface WithingsMeasurementGroup {
  /** Attribution */
  attrib: number;
  /** Category */
  category: number;
  /** Comment */
  comment?: string;
  /** Created date */
  created: number;
  /** Date */
  date: number;
  /** Device ID */
  deviceid: string;
  /** Group ID */
  grpid: number;
  /** Hash device ID */
  hash_deviceid: string;
  /** Measures */
  measures: WithingsApiMeasurement[];
}

export interface WithingsApiMeasurement {
  /** Algorithm */
  algo?: number;
  /** Firmware */
  fmw?: number;
  /** Type */
  type: number;
  /** Unit */
  unit: number;
  /** Value */
  value: number;
}

// NOTE: WithingsUserResponse and WithingsApiUser removed - not used in codebase
// Reason: User endpoint is not implemented by Withings API

export interface WithingsDeviceResponse {
  /** Body containing device data */
  body: {
    /** Devices */
    devices: WithingsApiDevice[];
  };
  /** Status */
  status: number;
}

export interface WithingsApiDevice {
  /** Battery */
  battery?: number;
  /** Firmware */
  firmware?: number;
  /** Device ID */
  id: string;
  /** Last sync time */
  last_sync_time?: number;
  /** Model */
  model: string;
  /** Type */
  type: string;
}

// API Request Types
export interface WithingsGetMeasurementsRequest {
  [key: string]: number | string | undefined;
  /** Action */
  action: "getmeas";
  /** Category (1 for real measures, 2 for user objectives) */
  category?: number;
  /** End date as unix timestamp */
  enddate?: number;
  /** Last update timestamp */
  lastupdate?: number;
  /** Measurement types (comma-separated) */
  meastypes?: string;
  /** Offset for pagination */
  offset?: number;
  /** Start date as unix timestamp */
  startdate?: number;
}

// NOTE: WithingsGetUserRequest removed - not used in codebase
// Reason: User endpoint is not implemented by Withings API

export interface WithingsGetDeviceRequest {
  [key: string]: number | string | undefined;
  /** Action */
  action: "get";
  /** User ID */
  userid: string;
}

// Measurement Type Constants (from Withings API documentation)
export const WITHINGS_MEASUREMENT_TYPES = {
  ATRIAL_FIBRILLATION: 130,
  ATRIAL_FIBRILLATION_PPG: 139,
  BASAL_METABOLIC_RATE: 226,
  BODY_TEMPERATURE: 71,
  BONE_MASS: 88,
  DIASTOLIC_BP: 9,
  ELECTROCHEMICAL_SKIN_CONDUCTANCE: 229,
  ELECTRODERMAL_ACTIVITY: 196,
  EXTRACELLULAR_WATER: 168,
  FAT_FREE_MASS: 5,
  FAT_MASS: 8,
  FAT_MASS_SEGMENTS: 174,
  FAT_RATIO: 6,
  HEART_PULSE: 11,
  HEIGHT: 4,
  HYDRATION: 77,
  INTRACELLULAR_WATER: 169,
  MUSCLE_MASS: 76,
  MUSCLE_MASS_SEGMENTS: 175,
  NERVE_HEALTH_SCORE: 167,
  PR_INTERVAL: 136,
  PULSE_WAVE_VELOCITY: 91,
  QRS_INTERVAL: 135,
  QT_CORRECTED: 138,
  QT_INTERVAL: 137,
  SKIN_TEMPERATURE: 73,
  SPO2: 54,
  SYSTOLIC_BP: 10,
  TEMPERATURE: 12,
  VASCULAR_AGE: 155,
  VISCERAL_FAT: 170,
  VO2_MAX: 123,
  WEIGHT: 1,
} as const;

// NOTE: WITHINGS_UNITS removed - not used in codebase
// Reason: Units are handled directly in measurement transformation
